import { BaseSelect, BASE_SELECT_MAX_OPTIONS_TO_SHOW } from '../../../../../forms/inputs/base-select';
import { Stack, Text, FormControl, DataList, DataItem } from '@cad-arc';
import { CautionsTabLocation } from '@mark43/cad-api';
import { useEffect, useMemo, useState } from 'react';
import { TabIndented } from '../../../../../panels/entity-panels/tabs/tab-indented';
import { TabSection } from '../../../../../panels/entity-panels/tabs/tab-section';
import { CautionPills } from '../caution-pills';
import { useEnabledFeatures } from '../../../../../common/hooks/enabled-features/use-enabled-features';
import { useApplicationType } from '../../../../../common/hooks/use-application-type';

const LocationCautions = ({
    data,
    title,
}: {
    data: CautionsTabLocation | undefined;
    title?: React.ReactNode;
}) => {
    const { isFirstResponder } = useApplicationType();

    if (!data && !title) {
        return null;
    }

    return (
        <Stack spacing={4}>
            {(title || data?.priority1Pills.length) && (
                <Stack spacing={1}>
                    {!!title && title}
                    {!!data?.priority1Pills.length && (
                        <CautionPills
                            cautions={data.priority1Pills}
                            size={isFirstResponder ? 'md' : undefined}
                        />
                    )}
                </Stack>
            )}
            {!!data?.pillGroups.length && (
                <DataList {...(isFirstResponder ? { variant: 'stacked' } : {})}>
                    {data.pillGroups.map((pillGroup) => {
                        return (
                            <DataItem
                                style={{
                                    ...(isFirstResponder
                                        ? { fontSize: 'var(--arc-fontSizes-md)' }
                                        : { alignItems: 'center' }),
                                }}
                                dataKey={pillGroup.name}
                                key={pillGroup.name}
                            >
                                <CautionPills
                                    cautions={pillGroup.cautionsTabPills}
                                    size={isFirstResponder ? 'md' : undefined}
                                />
                            </DataItem>
                        );
                    })}
                </DataList>
            )}
        </Stack>
    );
};

export const LocationsSection = ({
    data,
    isLoading,
}: {
    data: CautionsTabLocation | undefined;
    isLoading?: boolean;
}) => {
    const { isFirstResponder } = useApplicationType();

    const subpremiseOptions = useMemo(() => {
        return (
            data?.subPremiseCautionsTabLocations.flatMap((subPremiseCautionsTabLocation) => {
                if (subPremiseCautionsTabLocation.subPremise) {
                    return [
                        {
                            label: subPremiseCautionsTabLocation.subPremise,
                            value: subPremiseCautionsTabLocation.subPremise,
                        },
                    ];
                } else {
                    return [];
                }
            }) ?? []
        );
    }, [data]);

    // Renders all sub-premises in the dropdown; when there are more than the display limit we show a
    // hint under the field (rather than inside the menu, where match-sort would filter/re-rank it).
    const hasMoreSubpremisesThanLimit = subpremiseOptions.length > BASE_SELECT_MAX_OPTIONS_TO_SHOW;

    const [selectedSubpremise, setSelectedSubpremise] = useState('');
    const { subpremiseSupport } = useEnabledFeatures();

    const selectedSubpremiseCautionsTabLocation = data?.subPremiseCautionsTabLocations.find(
        (subPremiseCautionsTabLocation) => {
            return subPremiseCautionsTabLocation.subPremise === selectedSubpremise;
        }
    );

    const initialSelectedSubpremise = useMemo(() => {
        // Once our data loads, figure out if we have a selected subpremise
        if (data?.isEventLocationSubPremise) {
            return data.subPremise || '';
        } else {
            return (
                data?.subPremiseCautionsTabLocations.find((subpremiseCautionsTabLocation) => {
                    return subpremiseCautionsTabLocation.isEventLocationSubPremise;
                })?.subPremise || ''
            );
        }
    }, [data]);

    useEffect(() => {
        setSelectedSubpremise(initialSelectedSubpremise);
    }, [initialSelectedSubpremise]);

    const commonContent = (
        <Stack spacing={isFirstResponder ? 3 : 6} w="100%">
            <LocationCautions
                data={data}
                title={isFirstResponder ? null : <Text>{data?.addressWithSubPremise}</Text>}
            />
            <LocationCautions
                data={selectedSubpremiseCautionsTabLocation}
                title={
                    !!subpremiseOptions.length && (
                        <FormControl
                            label={subpremiseSupport ? 'Sub-premise' : 'Apt, Unit'}
                            htmlFor="subpremise"
                        >
                            <Stack spacing={1} align="stretch" w="100%">
                                <BaseSelect
                                    inputId="subpremise"
                                    value={selectedSubpremise}
                                    options={subpremiseOptions}
                                    isClearable={false}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        setSelectedSubpremise(e.target.value);
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                />
                                {hasMoreSubpremisesThanLimit && (
                                    <Text fontSize="sm" color="tertiary">
                                        {`Showing the first ${BASE_SELECT_MAX_OPTIONS_TO_SHOW} results. Type to refine your search.`}
                                    </Text>
                                )}
                            </Stack>
                        </FormControl>
                    )
                }
            />
        </Stack>
    );

    if (isFirstResponder) {
        return commonContent;
    }

    return (
        <TabSection title="Locations" loading={isLoading} emptyText="No Location Cautions Found">
            {data && <TabIndented>{commonContent}</TabIndented>}
        </TabSection>
    );
};
